import React, { useEffect, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { FeatureSwitch } from '../../../../offers/styles';
import { inputSelectCustomStyles, StyledSelect } from '../../../../../components/CustomSearchBar';
import { ErrorText } from '../../../../../components/utils/AvatarCropper';
import { Palette } from '../../../../../components/utils';
import { FieldBlock, FieldInnerFull, FieldLabel } from '../../../../../components/WizardV2/wizardStylesV2';
import {
  BlockingBlockSection,
  BlockingBlockTitle,
  BlockingPanel,
  FieldRest,
  ToggleCopy,
  ToggleDescription,
  ToggleRow,
  ToggleSwitchWrap,
  ToggleTitle,
  WizardTextInput
} from './addAmenityWizardStylesV2';
import {
  formatAutoBlockDurationLabel,
  isValidAutoBlockDuration,
  maxAllowedAutoBlockMinutes,
  normalizeSlotDurationMinutes,
  snapAutoBlockDuration
} from './addAmenityScheduleHelpersV2';

const AUTO_BLOCK_NO_ROOM_DEFAULT =
  'The hours of operation of your amenity must be long enough to contain at least one booking plus a minimum post-booking block duration. Please increase the hours of operation, or decrease the minimum booking duration';

function buildAutoBlockDurationOptions(slotMinutes, t, maxMinutes) {
  const options = [];
  for (let minutes = slotMinutes; minutes <= maxMinutes; minutes += slotMinutes) {
    options.push({
      value: minutes,
      label: formatAutoBlockDurationLabel(minutes, t)
    });
  }
  return options;
}

function AddAmenityBlockingStepV2({ t, control, errors, setValue, trigger, clearErrors }) {
  const td = (key, fallback) => t(key, { defaultValue: fallback });
  const blockingEnabled = useWatch({ control, name: 'enable_post_booking_auto_block' });
  const slotDurationMinutes = useWatch({ control, name: 'slot_duration_minutes' }) ?? 30;
  const autoBlockDuration = useWatch({ control, name: 'post_booking_auto_block_duration' });
  const bookingLimitsUnit = useWatch({ control, name: 'booking_limits_unit' });
  const allowMultiDay = useWatch({ control, name: 'allow_multi_day' });
  const selectedDays = useWatch({ control, name: 'selectedDays' });
  const dailySchedule = useWatch({ control, name: 'daily_schedule' });
  const minDurationValue = useWatch({ control, name: 'min_duration_value' });
  const minReservationHours = useWatch({ control, name: 'min_reservation_hours' });

  const slotMinutes = normalizeSlotDurationMinutes(slotDurationMinutes);
  const maxAllowedMinutes = useMemo(
    () =>
      maxAllowedAutoBlockMinutes({
        bookingLimitsUnit,
        allowMultiDay: !!allowMultiDay,
        slotMinutes,
        selectedDays,
        dailySchedule,
        minDurationValue,
        minReservationHours
      }),
    [
      allowMultiDay,
      bookingLimitsUnit,
      dailySchedule,
      minDurationValue,
      minReservationHours,
      selectedDays,
      slotMinutes
    ]
  );
  const durationOptions = useMemo(
    () => buildAutoBlockDurationOptions(slotMinutes, t, maxAllowedMinutes),
    [maxAllowedMinutes, slotMinutes, t]
  );
  const noRoomMessage = useMemo(
    () =>
      t('add_amenity_auto_block_no_room', {
        defaultValue: AUTO_BLOCK_NO_ROOM_DEFAULT
      }),
    [t]
  );

  // Keep errors in sync when schedule / min / slot change (Update can keep both sections open).
  useEffect(() => {
    if (!blockingEnabled) {
      if (clearErrors) {
        clearErrors('post_booking_auto_block_duration');
      }
      return;
    }
    if (trigger) {
      trigger('post_booking_auto_block_duration');
    }
  }, [
    autoBlockDuration,
    blockingEnabled,
    clearErrors,
    maxAllowedMinutes,
    slotMinutes,
    trigger
  ]);

  const durationSelectStyles = useMemo(() => {
    const base = inputSelectCustomStyles({ minHeight: '56px', height: 'auto' });
    if (!errors?.post_booking_auto_block_duration) return base;
    return {
      ...base,
      control: (styles, state) => ({
        ...base.control(styles, state),
        border: `2px solid ${Palette.ERROR}`
      })
    };
  }, [errors?.post_booking_auto_block_duration]);

  return (
    <>
      <ToggleRow>
        <ToggleCopy>
          <ToggleTitle>
            {t('add_amenity_enable_post_reservation_blocking', {
              defaultValue: 'Enable Post Reservation Blocking'
            })}
          </ToggleTitle>
          <ToggleDescription>
            {td(
              'add_amenity_post_reservation_blocking_desc',
              'After each booking, automatically block the amenity for cleanup. Longer times reserve the end of each open period and reduce slots others can book.'
            )}
          </ToggleDescription>
        </ToggleCopy>
        <Controller
          control={control}
          name="enable_post_booking_auto_block"
          render={({ field: { onChange, value } }) => (
            <ToggleSwitchWrap>
              <FeatureSwitch
                shape="pill"
                color="primary"
                checked={!!value}
                onChange={(e) => {
                  const checked = e.target.checked;
                  onChange(checked);
                  if (
                    checked &&
                    setValue &&
                    !isValidAutoBlockDuration(autoBlockDuration, slotMinutes, maxAllowedMinutes)
                  ) {
                    setValue(
                      'post_booking_auto_block_duration',
                      snapAutoBlockDuration(autoBlockDuration, slotMinutes, maxAllowedMinutes),
                      { shouldDirty: true, shouldValidate: true }
                    );
                  }
                }}
              />
            </ToggleSwitchWrap>
          )}
        />
      </ToggleRow>

      {blockingEnabled && (
        <BlockingPanel>
          <BlockingBlockSection>
            <BlockingBlockTitle>
              {t('add_amenity_block_single_title', { defaultValue: 'Post-booking block' })}
            </BlockingBlockTitle>

            <FieldBlock>
              <FieldInnerFull>
                <FieldLabel>
                  {t('add_amenity_block_time', { defaultValue: 'Block Time' })}
                </FieldLabel>
                <Controller
                  control={control}
                  name="post_booking_auto_block_duration"
                  render={({ field: { onChange, onBlur, value, ref } }) => {
                    const numeric = value != null && value !== '' ? Number(value) : slotMinutes;
                    const selected =
                      durationOptions.find((o) => o.value === numeric) ||
                      (Number.isFinite(numeric) && numeric > 0
                        ? { value: numeric, label: formatAutoBlockDurationLabel(numeric, t) }
                        : durationOptions[0] || null);
                    return (
                      <StyledSelect
                        ref={ref}
                        inputId="wizard-post-booking-block-time"
                        instanceId="amenity-post-booking-block-time"
                        options={durationOptions}
                        value={selected}
                        onChange={(opt) => onChange(opt != null ? opt.value : slotMinutes)}
                        onBlur={onBlur}
                        styles={durationSelectStyles}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isClearable={false}
                        isSearchable={false}
                        isDisabled={durationOptions.length === 0}
                        menuPlacement="auto"
                        aria-label={t('add_amenity_block_time', { defaultValue: 'Block Time' })}
                      />
                    );
                  }}
                />
              </FieldInnerFull>
              {durationOptions.length === 0 && <ErrorText>{noRoomMessage}</ErrorText>}
              {durationOptions.length > 0 && errors?.post_booking_auto_block_duration && (
                <ErrorText>{errors.post_booking_auto_block_duration.message}</ErrorText>
              )}
            </FieldBlock>

            <FieldBlock>
              <FieldInnerFull>
                <FieldLabel>{t('Reason')}</FieldLabel>
                <FieldRest>
                  <Controller
                    control={control}
                    name="post_block_description"
                    render={({ field: { onChange, value } }) => (
                      <WizardTextInput
                        type="text"
                        id="wizard-post-booking-block-reason"
                        autoComplete="off"
                        value={value ?? ''}
                        onChange={onChange}
                        placeholder={t('add_amenity_block_reason_placeholder', {
                          defaultValue: 'e.g. Clean massage room after use'
                        })}
                        aria-label={t('Reason')}
                      />
                    )}
                  />
                </FieldRest>
              </FieldInnerFull>
            </FieldBlock>
          </BlockingBlockSection>
        </BlockingPanel>
      )}
    </>
  );
}

export default AddAmenityBlockingStepV2;
